import { useLayoutEffect, useState, useEffect } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { NetworkProvider, useNetwork } from './context/NetworkContext';
import { NetworkBanner } from './components/NetworkBanner';
import { setNetworkStatusCallback } from './api/client';
import Header from './components/Header';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import Home from './pages/Home';
import Catalogue from './pages/Catalogue';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import QuoteRequestPage from './pages/QuoteRequestPage';
import ResellerPage from './pages/ResellerPage';
import ServicesPage from './pages/ServicesPage';
import AboutPage from './pages/AboutPage';
import OrdersPage from './pages/OrdersPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResellerDashboard from './pages/ResellerDashboard';
import ResellerCatalogue from './pages/ResellerCatalogue';
import ResellerOrders from './pages/ResellerOrders';
import ResellerQuotes from './pages/ResellerQuotes';
import ResellerQuoteDetail from './pages/ResellerQuoteDetail';
import ResellerAccount from './pages/ResellerAccount';
import AdminDashboard from './pages/AdminDashboard';
import AdminProducts from './pages/AdminProducts';
import AdminUsers from './pages/AdminUsers';
import AdminResellerApplications from './pages/AdminResellerApplications';
import AdminOrders from './pages/AdminOrders';
import AdminQuotes from './pages/AdminQuotes';
import AdminContact from './pages/AdminContact';
import AdminCategories from './pages/AdminCategories';
import SetPasswordPage from './pages/SetPasswordPage';

const protectedPages = ['orders', 'checkout', 'quote-request'];
const resellerPages = ['reseller-dashboard', 'reseller-catalogue', 'reseller-orders', 'reseller-quotes', 'reseller-quote-detail', 'reseller-account'];
const adminPages = ['admin-dashboard', 'admin-products', 'admin-categories', 'admin-users', 'admin-reseller-applications', 'admin-orders', 'admin-quotes', 'admin-contact'];

const pageToUrl: Record<string, string> = {
  home: '/',
  catalogue: '/products',
  cart: '/cart',
  checkout: '/checkout',
  'quote-request': '/quote-request',
  reseller: '/revendeurs',
  services: '/services',
  about: '/about',
  orders: '/orders',
  login: '/login',
  register: '/register',
  'forgot-password': '/forgot-password',
  'reseller-dashboard': '/reseller/dashboard',
  'reseller-catalogue': '/reseller/catalogue',
  'reseller-orders': '/reseller/orders',
  'reseller-quotes': '/reseller/quotes',
  'reseller-quote-detail': '/reseller/quotes/detail',
  'reseller-account': '/reseller/account',
  'admin-dashboard': '/admin/dashboard',
  'admin-products': '/admin/products',
  'admin-categories': '/admin/categories',
  'admin-users': '/admin/users',
  'admin-reseller-applications': '/admin/reseller-applications',
  'admin-orders': '/admin/orders',
  'admin-quotes': '/admin/quotes',
  'admin-contact': '/admin/contact',
  'set-password': '/set-password',
  'verify-email': '/verify-email',
};

const urlToPage: Record<string, string> = {
  '/': 'home',
  '/products': 'catalogue',
  '/cart': 'cart',
  '/checkout': 'checkout',
  '/quote-request': 'quote-request',
  '/revendeurs': 'reseller',
  '/services': 'services',
  '/about': 'about',
  '/orders': 'orders',
  '/login': 'login',
  '/register': 'register',
  '/forgot-password': 'forgot-password',
  '/reseller/dashboard': 'reseller-dashboard',
  '/reseller/catalogue': 'reseller-catalogue',
  '/reseller/orders': 'reseller-orders',
  '/reseller/quotes': 'reseller-quotes',
  '/reseller/quotes/detail': 'reseller-quote-detail',
  '/reseller/account': 'reseller-account',
  '/admin/dashboard': 'admin-dashboard',
  '/admin/products': 'admin-products',
  '/admin/categories': 'admin-categories',
  '/admin/users': 'admin-users',
  '/admin/reseller-applications': 'admin-reseller-applications',
  '/admin/orders': 'admin-orders',
  '/admin/quotes': 'admin-quotes',
  '/admin/contact': 'admin-contact',
  '/set-password': 'set-password',
  '/verify-email': 'verify-email',
};

function ForbiddenPage() {
  const { lang, setPage } = useApp();
  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center p-8">
        <p className="text-6xl font-bold text-gray-200 mb-4">403</p>
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          {lang === 'fr' ? 'Accès non autorisé' : 'Access denied'}
        </h1>
        <p className="text-gray-500 mb-6">
          {lang === 'fr'
            ? "Vous n'avez pas les droits pour accéder à cette page."
            : 'You do not have permission to access this page.'}
        </p>
        <button
          onClick={() => setPage('home')}
          className="px-5 py-2 bg-lodiss-red text-white rounded-lg text-sm font-semibold hover:bg-lodiss-dark transition-colors"
        >
          {lang === 'fr' ? "Retour à l'accueil" : 'Back to home'}
        </button>
      </div>
    </main>
  );
}

function PageRouter() {
  const { page, setPage, currentUser, setRedirectAfterLogin, isLoading } = useApp();

  // Guard: handle role-based cross-zone navigation (only after auth resolves)
  useLayoutEffect(() => {
    if (isLoading) return; // Wait until auth state is known

    const adminPageSet = new Set(adminPages);
    const resellerPageSet = new Set(resellerPages);
    const protectedPageSet = new Set(protectedPages);

    // Unauthenticated user trying to access protected routes
    if (!currentUser) {
      if (page === 'verify-email') {
        return;
      }
      if (adminPageSet.has(page) || resellerPageSet.has(page) || protectedPageSet.has(page)) {
        setRedirectAfterLogin(page);
        setPage('login');
      }
      return;
    }

    const role = currentUser.role;

    // Admin page accessed by non-admin
    if (adminPageSet.has(page) && role !== 'super_admin') {
      setPage(role === 'reseller' ? 'reseller-dashboard' : 'catalogue');
      return;
    }

    // Reseller page accessed by non-reseller/non-admin
    if (resellerPageSet.has(page) && role !== 'reseller' && role !== 'super_admin') {
      setPage('catalogue');
      return;
    }

  }, [page, currentUser, isLoading]);

  // Sync URL bar
  useLayoutEffect(() => {
    const url = pageToUrl[page];
    const currentPath = window.location.pathname;
    
    // Skip if it's a dynamic route (product page with ID)
    if (page === 'product' && currentPath.startsWith('/product/') && currentPath !== '/product') {
      return;
    }
    
    if (url && currentPath !== url && !(currentPath === '/products' && page === 'home')) {
      window.history.replaceState({}, '', url);
    }
  }, [page]);

  // Show loading spinner while auth resolves
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" />
      </div>
    );
  }

  // Admin layout — wrapped in auth check
  if (adminPages.includes(page)) {
    if (!currentUser || currentUser.role !== 'super_admin') {
      return <ForbiddenPage />;
    }
    const adminPageMap: Record<string, React.ReactNode> = {
      'admin-dashboard': <AdminDashboard />,
      'admin-products': <AdminProducts />,
      'admin-categories': <AdminCategories />,
      'admin-users': <AdminUsers />,
      'admin-reseller-applications': <AdminResellerApplications />,
      'admin-orders': <AdminOrders />,
      'admin-quotes': <AdminQuotes />,
      'admin-contact': <AdminContact />,
    };
    return <>{adminPageMap[page]}</>;
  }

  // All other pages
  const pageMap: Record<string, React.ReactNode> = {
    home: <Home />,
    catalogue: <Catalogue />,
    product: <ProductPage />,
    cart: <CartPage />,
    checkout: <CheckoutPage />,
    'quote-request': <QuoteRequestPage />,
    reseller: <ResellerPage />,
    services: <ServicesPage />,
    about: <AboutPage />,
    orders: <OrdersPage />,
    login: <LoginPage />,
    register: <RegisterPage />,
    'verify-email': <VerifyEmailPage />,
    'forgot-password': <ForgotPasswordPage />,
    'reseller-dashboard': <ResellerDashboard />,
    'reseller-catalogue': <ResellerCatalogue />,
    'reseller-orders': <ResellerOrders />,
    'reseller-quotes': <ResellerQuotes />,
    'reseller-quote-detail': <ResellerQuoteDetail />,
    'reseller-account': <ResellerAccount />,
    'set-password': <SetPasswordPage />,
    'reset-password': <SetPasswordPage />,
  };

  return <>{pageMap[page] ?? <Home />}</>;
}

function AppContent() {
  const { page } = useApp();
  const isAdminPage = adminPages.includes(page);

  return (
    <div className="flex flex-col min-h-screen">
      {!isAdminPage && <Header />}
      <div className="flex-1"><PageRouter /></div>
      {!isAdminPage && <Footer />}
      {!isAdminPage && <WhatsAppButton />}
    </div>
  );
}

function NetworkBannerWrapper() {
  const { status, checkConnection } = useNetwork();
  const { lang } = useApp();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    setNetworkStatusCallback((newStatus) => {
      if (newStatus === 'connected') {
        setShowBanner(false);
      } else if (newStatus === 'disconnected' || newStatus === 'reconnecting') {
        setShowBanner(true);
      }
    });
  }, []);

  const handleRetry = async () => {
    const isOk = await checkConnection();
    if (isOk) {
      setShowBanner(false);
    }
  };

  return <NetworkBanner isVisible={showBanner} onRetry={handleRetry} lang={lang} />;
}

export default function App() {
  return (
    <NetworkProvider>
      <AppProvider>
        <NetworkBannerWrapper />
        <AppContent />
      </AppProvider>
    </NetworkProvider>
  );
}
